import type { ExtractedImage, FieldReview, WpImportProperty } from './types';
import {
  POST_CONTENT_HEADERS,
  POST_ID_HEADERS,
  POST_TITLE_HEADERS,
} from './types';
import { wpContentToHtml } from './contentClean';
import { extractPathsFromUnserialized, parseMetaValue } from './phpMeta';

function rowKeyLookup(row: Record<string, string>, target: string): string | undefined {
  const t = target.toLowerCase();
  for (const k of Object.keys(row)) {
    if (k.trim().toLowerCase() === t) return k;
  }
  return undefined;
}

export function findCol(row: Record<string, string>, candidates: readonly string[]): string {
  for (const c of candidates) {
    const key = rowKeyLookup(row, c) ?? (Object.prototype.hasOwnProperty.call(row, c) ? c : undefined);
    if (key != null) {
      const v = row[key];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
  }
  return '';
}

export function findPostIdColumn(headers: string[]): string | null {
  const lower = headers.map((h) => h.trim().toLowerCase());
  for (const c of POST_ID_HEADERS) {
    const idx = lower.indexOf(c.toLowerCase());
    if (idx >= 0) return headers[idx].trim();
  }
  return null;
}

export function validateHeaders(headers: string[]): { ok: boolean; missing: string[] } {
  const lower = headers.map((h) => h.trim().toLowerCase());
  const hasMetaKey = lower.includes('meta_key');
  const hasMetaValue = lower.includes('meta_value');
  const missing: string[] = [];

  if (hasMetaKey || hasMetaValue) {
    if (!hasMetaKey) missing.push('meta_key');
    if (!hasMetaValue) missing.push('meta_value');
    const hasId = POST_ID_HEADERS.some((id) => lower.includes(id.toLowerCase()));
    if (!hasId) missing.push('ID or post_id (to group meta rows per property)');
  } else {
    const hasTitle = POST_TITLE_HEADERS.some((t) => lower.includes(t.toLowerCase()));
    if (!hasTitle) missing.push('post_title or title');
  }
  return { ok: missing.length === 0, missing };
}

function buildImageList(wpPostId: string, paths: string[]): ExtractedImage[] {
  const uniq = [...new Set(paths.filter(Boolean))];
  let i = 0;
  return uniq.map((p) => ({
    id: `${wpPostId}-img-${i++}`,
    url: p,
    source: 'attachment / meta',
    raw: p,
  }));
}

function collectImagesFromMeta(key: string, rawVal: string, value: unknown, imageCandidates: string[]) {
  if (key === '_wp_attached_file') {
    if (typeof value === 'string') imageCandidates.push(value);
    imageCandidates.push(rawVal);
  }
  if (key === '_wp_attachment_metadata' || key.toLowerCase().includes('attachment')) {
    extractPathsFromUnserialized(value).forEach((p) => imageCandidates.push(p));
  }
  if (
    key.toLowerCase().includes('gallery') ||
    key.toLowerCase().includes('image')
  ) {
    extractPathsFromUnserialized(value).forEach((p) => imageCandidates.push(p));
    if (typeof value === 'string') imageCandidates.push(value);
  }
}

export function applyBaseUrlToImages(images: ExtractedImage[], baseUrl: string): ExtractedImage[] {
  const base = baseUrl.replace(/\/$/, '');
  if (!base) return images;
  return images.map((img) => {
    const raw = img.raw ?? img.url;
    if (raw.startsWith('http')) return { ...img, url: raw };
    const path = raw.replace(/^\//, '').replace(/^wp-content\/uploads\//, '');
    return { ...img, url: `${base}/wp-content/uploads/${path}` };
  });
}

function buildPropertyFromMetaRows(
  wpPostId: string,
  group: Record<string, string>[],
  _idCol: string
): WpImportProperty {
  const meta: Record<string, unknown> = {};
  const rawMeta: Record<string, string> = {};
  let postTitle = '';
  let postContent = '';
  let hadSerialized = false;
  const reviews: FieldReview[] = [];
  const imageCandidates: string[] = [];

  for (const row of group) {
    const title = findCol(row, POST_TITLE_HEADERS);
    const content = findCol(row, POST_CONTENT_HEADERS);
    if (title) postTitle = title;
    if (content) postContent = content;

    const mkKey = rowKeyLookup(row, 'meta_key');
    const mvKey = rowKeyLookup(row, 'meta_value');
    if (!mkKey || !mvKey) continue;
    const mk = String(row[mkKey] ?? '').trim();
    const rawVal = String(row[mvKey] ?? '');
    if (!mk) continue;
    rawMeta[mk] = rawVal;
    const { value, wasSerialized } = parseMetaValue(rawVal);
    if (wasSerialized) hadSerialized = true;
    meta[mk] = value;
    collectImagesFromMeta(mk, rawVal, value, imageCandidates);
    if (wasSerialized) {
      extractPathsFromUnserialized(value).forEach((p) => imageCandidates.push(p));
    }
  }

  if (!postTitle) {
    reviews.push({
      field: 'post_title',
      message: 'Missing post_title in CSV for this ID; using placeholder',
      severity: 'warn',
    });
    postTitle = `Imported listing ${wpPostId}`;
  }

  const images = buildImageList(wpPostId, imageCandidates);

  return {
    wpPostId,
    postTitle,
    postContent,
    postContentHtml: wpContentToHtml(postContent),
    meta,
    rawMeta,
    images,
    reviews,
    hadSerializedMeta: hadSerialized,
    selected: true,
    selectedImageIds: new Set(images.map((i) => i.id)),
  };
}

export function rowsToLongFormat(
  rows: Record<string, string>[],
  headers: string[]
): WpImportProperty[] {
  const idCol = findPostIdColumn(headers);
  if (!idCol) return [];
  const byId = new Map<string, Record<string, string>[]>();
  for (const row of rows) {
    const id = String(row[idCol] ?? '').trim();
    if (!id) continue;
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id)!.push(row);
  }
  const out: WpImportProperty[] = [];
  for (const [wpPostId, group] of byId) {
    out.push(buildPropertyFromMetaRows(wpPostId, group, idCol));
  }
  return out;
}

const SKIP_WIDE_KEYS = new Set(
  [...POST_ID_HEADERS, ...POST_TITLE_HEADERS, ...POST_CONTENT_HEADERS].map((k) => k.toLowerCase())
);

export function rowsToWideFormat(rows: Record<string, string>[]): WpImportProperty[] {
  return rows.map((row, idx) => {
    const wpPostId = findCol(row, POST_ID_HEADERS) || `import-row-${idx + 1}`;
    let postTitle = findCol(row, POST_TITLE_HEADERS);
    let postContent = findCol(row, POST_CONTENT_HEADERS);
    const reviews: FieldReview[] = [];
    if (!postTitle) {
      postTitle = `Imported row ${idx + 1}`;
      reviews.push({ field: 'post_title', message: 'Missing title column', severity: 'warn' });
    }
    const meta: Record<string, unknown> = {};
    const rawMeta: Record<string, string> = {};
    let hadSerialized = false;
    const imageCandidates: string[] = [];

    for (const [k, v] of Object.entries(row)) {
      const key = k.trim();
      if (SKIP_WIDE_KEYS.has(key.toLowerCase())) continue;
      const rawVal = String(v ?? '');
      rawMeta[key] = rawVal;
      const { value, wasSerialized } = parseMetaValue(rawVal);
      if (wasSerialized) hadSerialized = true;
      meta[key] = value;
      collectImagesFromMeta(key, rawVal, value, imageCandidates);
      if (wasSerialized) extractPathsFromUnserialized(value).forEach((p) => imageCandidates.push(p));
    }

    const images = buildImageList(wpPostId, imageCandidates);
    return {
      wpPostId,
      postTitle,
      postContent,
      postContentHtml: wpContentToHtml(postContent),
      meta,
      rawMeta,
      images,
      reviews,
      hadSerializedMeta: hadSerialized,
      selected: true,
      selectedImageIds: new Set(images.map((i) => i.id)),
    };
  });
}
